/**
 * Insurance disclaimer + acknowledgement checkboxes (Tx Presenter) - pure rules.
 *
 * WHAT: an office can show a plain-text disclaimer paragraph and up to 10
 * "I understand ..." checkbox lines above the signature, on the in-office sign
 * panel, the patient link, and the signed PDF. This file is the whole decision
 * logic: normalize what is stored, resolve what a patient sees, decide which
 * required lines are still unticked, and freeze the signed record.
 *
 * SCOPE: practice-wide only (Settings -> Presentation). There is no per-plan or
 * per-patient override, by design - so unlike resolveDisplay.js there is no
 * layering here, just the one stored blob at TX_PRESENTER_SETTINGS_PRESENTATION
 * .disclaimer.
 *
 * DEFAULT OFF: absent blob, absent key, or showText not strictly true with no
 * lines => resolvePatientDisclaimer returns null and every surface behaves
 * exactly as it did before this feature existed (the built-in checkbox, the
 * default consent sentence on the PDF). Nothing changes for an office until it
 * edits the card.
 *
 * TEXT: plain, never rich. Newlines are kept (\r\n and \r become \n); there is no
 * markup, because the PDF renders in Helvetica and the panels use
 * white-space: pre-line.
 *
 * TWIN RULE: IMPORT-FREE on purpose - the node-tests execute this exact file.
 * The backend twin is teamio-serverless-api/tx-presenter-service/lib/disclaimer.js.
 * Everything between the SHARED BODY markers is BYTE-IDENTICAL across the two
 * files and pinned by cypress/node-tests/tx-presenter-disclaimer.test.js (and the
 * backend's tests/disclaimer-rules.test.js), which read BOTH files and compare
 * their shared bodies. Change both, or the parity test fails.
 */

// ─────────────────────────── SHARED BODY START ───────────────────────────

const DISCLAIMER_LIMITS = { TEXT_MAX: 4000, LINE_MAX: 300, MAX_LINES: 10 };

function isPlainObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

// Only string entries of an array count as "acknowledged"; anything else (not an
// array, numbers, objects, null) is an empty list. Fail-closed: a malformed
// payload can never tick a required box for the patient.
function toIdSet(acknowledgedIds) {
  const set = new Set();
  if (Array.isArray(acknowledgedIds)) {
    acknowledgedIds.forEach((id) => {
      if (typeof id === 'string') set.add(id);
    });
  }
  return set;
}

// Canonical shape of the stored blob. Everything stored is run through this on
// save (backend) and on read (frontend store getter), so a hand-edited or
// partially-written blob can never reach a patient or the PDF in a surprising
// shape. Never throws.
//   showText         strict `=== true` (absent / 'true' / 1 are all OFF)
//   text             plain; \r\n and \r become \n; trimmed; capped at TEXT_MAX
//   acknowledgements only plain-object rows with non-blank text survive; capped
//                    at MAX_LINES AFTER blanks are dropped. `required` defaults
//                    to true (only an explicit `false` makes a line optional).
//                    A missing/blank id becomes 'ack_<position + 1>' (position in
//                    the raw array); duplicate ids get '_2', '_3', ... appended.
function normalizeDisclaimer(raw) {
  if (!isPlainObject(raw)) return { showText: false, text: '', acknowledgements: [] };

  const text = String(raw.text == null ? '' : raw.text)
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim()
    .slice(0, DISCLAIMER_LIMITS.TEXT_MAX)
    .replace(/\s+$/, ''); // a cut that lands on whitespace must not make re-normalizing change the value

  const rows = Array.isArray(raw.acknowledgements) ? raw.acknowledgements : [];
  const acknowledgements = [];
  const usedIds = new Set();

  for (let i = 0; i < rows.length && acknowledgements.length < DISCLAIMER_LIMITS.MAX_LINES; i++) {
    const row = rows[i];
    if (!isPlainObject(row)) continue;

    const lineText = String(row.text == null ? '' : row.text)
      .trim()
      .slice(0, DISCLAIMER_LIMITS.LINE_MAX)
      .replace(/\s+$/, '');
    if (lineText === '') continue;

    const givenId = typeof row.id === 'string' ? row.id.trim() : '';
    const baseId = givenId !== '' ? givenId : 'ack_' + (i + 1);
    let id = baseId;
    for (let n = 2; usedIds.has(id); n++) id = baseId + '_' + n;
    usedIds.add(id);

    acknowledgements.push({ id, text: lineText, required: row.required !== false });
  }

  return { showText: raw.showText === true, text, acknowledgements };
}

// What the patient surfaces (Present Mode, patient link) and the signed PDF
// consume. `presentationBlob` is the practice's PRESENTATION settings blob (or
// anything shaped like it). Returns null when there is nothing to show — no
// visible paragraph AND no lines — which is exactly the "feature OFF" state.
// `showText:false` hides only the paragraph; the checkbox lines always show.
function resolvePatientDisclaimer(presentationBlob) {
  const n = normalizeDisclaimer(presentationBlob && presentationBlob.disclaimer);
  const text = n.showText ? n.text : '';
  if (text === '' && n.acknowledgements.length === 0) return null;
  return {
    text,
    acknowledgements: n.acknowledgements.map((a) => ({ id: a.id, text: a.text, required: a.required })),
  };
}

// Ids of REQUIRED lines the patient has not ticked ([] when nothing is missing
// or there is nothing to acknowledge). A line with no explicit `required:false`
// is treated as required, mirroring normalizeDisclaimer's default. Optional
// lines never block a signature.
function missingRequiredAcks(resolved, acknowledgedIds) {
  if (!resolved || !Array.isArray(resolved.acknowledgements)) return [];
  const got = toIdSet(acknowledgedIds);
  return resolved.acknowledgements
    .filter((a) => a && a.required !== false && !got.has(a.id))
    .map((a) => a.id);
}

// True iff the office configured at least one acknowledgement line. This is the
// switch that replaces Teamio's built-in "I confirm I've reviewed..." checkbox
// and the PDF's default consent sentence: the office's own checked lines become
// the record instead.
function hasOfficeLines(resolved) {
  return !!(resolved && Array.isArray(resolved.acknowledgements) && resolved.acknowledgements.length > 0);
}

// The record stamped into the signed plan at sign time (PlanSnapshot.disclaimer):
// the resolved text and lines exactly as the patient saw them, each line with
// whether THIS patient ticked it. null when there was nothing to show.
function freezeDisclaimer(resolved, acknowledgedIds) {
  if (!resolved) return null;
  const got = toIdSet(acknowledgedIds);
  const lines = Array.isArray(resolved.acknowledgements) ? resolved.acknowledgements : [];
  return {
    text: String(resolved.text == null ? '' : resolved.text),
    acknowledgements: lines.map((a) => ({
      id: a.id,
      text: a.text,
      required: a.required !== false,
      checked: got.has(a.id),
    })),
  };
}

// ──────────────────────────── SHARED BODY END ────────────────────────────

export {
  DISCLAIMER_LIMITS,
  normalizeDisclaimer,
  resolvePatientDisclaimer,
  missingRequiredAcks,
  hasOfficeLines,
  freezeDisclaimer,
};
